import type { ActivityRecord } from '../model/activity.js'
import { semicirclesToDegrees } from '../model/units.js'
import { hasGps, haversineMeters } from './distance.js'

/**
 * GPS outlier (jump) detection — the master project brief explicitly
 * lists "GPS outlier detection" under GPS point editing (§6.8); this did
 * not exist anywhere in the codebase before an independent review
 * flagged it as a required, entirely-missing capability (2026-10-25).
 * `edit/operations.ts`'s `repairOutlier` operation already existed, but
 * only applies a fix to a point the *caller* has already identified —
 * this module is what identifies the candidate in the first place.
 *
 * Heuristic: flag record `i` as a jump candidate when both the
 * incoming (i-1 -> i) and outgoing (i -> i+1) implied speeds exceed
 * `thresholdMetersPerSecond`, but the direct speed skipping over `i`
 * (i-1 -> i+1) does not. That signature — a spike away and back —
 * specifically catches an isolated bad GPS fix, as distinct from a
 * genuinely fast *sustained* activity (e.g. a fast descent), where
 * every consecutive speed would be elevated, not just one point's
 * immediate neighbors. Automatic *detection* is clearly separated from
 * *confirmed edits* (the master spec's "distinguish automatic
 * suggestions from confirmed edits" requirement) — this module only
 * returns candidates; nothing here mutates the activity. The caller
 * decides whether to act on a candidate via `repairOutlier`,
 * `setGpsConstant`, or `interpolateGps`.
 */

export interface GpsOutlierCandidate {
  index: number
  timestamp: Date | undefined
  incomingSpeedMetersPerSecond: number
  outgoingSpeedMetersPerSecond: number
}

/** Implausible for ordinary human-powered activity (~54 km/h); a reasonable default, not a hard rule. */
export const DEFAULT_OUTLIER_SPEED_THRESHOLD_MPS = 15

function impliedSpeed(a: ActivityRecord, b: ActivityRecord): number | undefined {
  if (!hasGps(a) || !hasGps(b)) return undefined
  if (!(a.timestamp instanceof Date) || !(b.timestamp instanceof Date)) return undefined
  const dtSec = (b.timestamp.getTime() - a.timestamp.getTime()) / 1000
  if (dtSec <= 0) return undefined
  const distance = haversineMeters(
    { lat: semicirclesToDegrees(a.positionLat), lon: semicirclesToDegrees(a.positionLong) },
    { lat: semicirclesToDegrees(b.positionLat), lon: semicirclesToDegrees(b.positionLong) },
  )
  return distance / dtSec
}

export function detectGpsOutliers(
  records: ActivityRecord[],
  thresholdMetersPerSecond: number = DEFAULT_OUTLIER_SPEED_THRESHOLD_MPS,
): GpsOutlierCandidate[] {
  const candidates: GpsOutlierCandidate[] = []

  for (let i = 1; i < records.length - 1; i++) {
    const prev = records[i - 1]
    const curr = records[i]
    const next = records[i + 1]
    if (prev == null || curr == null || next == null) continue

    const incoming = impliedSpeed(prev, curr)
    const outgoing = impliedSpeed(curr, next)
    if (incoming == null || outgoing == null) continue
    if (incoming <= thresholdMetersPerSecond || outgoing <= thresholdMetersPerSecond) continue

    const direct = impliedSpeed(prev, next)
    if (direct != null && direct > thresholdMetersPerSecond) continue // sustained fast motion, not an isolated jump

    candidates.push({
      index: i,
      timestamp: curr.timestamp instanceof Date ? curr.timestamp : undefined,
      incomingSpeedMetersPerSecond: incoming,
      outgoingSpeedMetersPerSecond: outgoing,
    })
  }

  return candidates
}
